<template>
  <v-content>
    <v-container fill-height>
      <v-layout
        column
        align-space-between
        justify-space-between
        fill-height
      >
        <v-flex>
          <v-layout xs12>
            <v-flex xs4>
              <v-img
                px-0
                :contain="true"
                :src="require('@/assets/images/logo.png')"
                max-width="100px"
              />
            </v-flex>
            <v-spacer />
          </v-layout>
        </v-flex>
        <v-flex mt-5>
          <v-layout
            wrap
            align-center
            xs12
          >
            <v-flex
              xs12
              md4
            >
              <h1>{{ $t('message.welcome.1') }}<v-spacer />{{ $t('message.welcome.2') }}</h1>
            </v-flex>
            <v-spacer />
            <v-flex
              xs12
              md5
              lg4
            >
              <v-card>
                <v-toolbar
                  dark
                  color="szpRed"
                >
                  <v-toolbar-title>{{ $t('message.title_login') }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text dark>
                  <form @keyup.enter="submit()">
                    <v-text-field
                      id="login"
                      v-model="email"
                      v-validate="validationRules.get('regex_email_without_polish')"
                      :error-messages="errors.collect('regex_email_without_polish')"
                      :data-vv-as="$t('label.email')"
                      color="szpRed"
                      data-vv-name="email"
                      inputmode="email"
                      :hide-details="hasError"
                      prepend-icon="person"
                      name="email"
                      :placeholder="$t('label.email')"
                      autofocus
                      @click="resetEmailValidation()"
                    />

                    <v-text-field
                      id="password"
                      v-model="formData.password"
                      v-validate="'required'"
                      :error-messages="errors.collect('password')"
                      :data-vv-as="$t('label.password')"
                      data-vv-name="password"
                      color="szpRed"
                      prepend-icon="lock"
                      name="password"
                      type="password"
                      autocomplete="current-password"
                      :placeholder="$t('label.password')"
                    />
                  </form>
                </v-card-text>
                <v-card-actions>
                  <v-layout
                    wrap
                    column
                    align-end
                    justify-center
                    mr-3
                  >
                    <v-flex xs12>
                      <v-btn
                        id="submitButton"
                        color="szpRed"
                        dark
                        :loading="isLoading"
                        @click.native="submit"
                      >
                        {{ $t('message.login_button') }}
                      </v-btn>
                    </v-flex>
                    <v-flex
                      xs12
                      my-1
                    >
                      <a
                        class="link"
                        :href="env.forgotPassword"
                      >{{
                        $t('message.forgot_password')
                      }}</a>
                    </v-flex>
                  </v-layout>
                </v-card-actions>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
        <AppFooter />
      </v-layout>
      <v-dialog
        v-model="modalLogoutInfo"
        max-width="500px"
        :transition="false"
      >
        <v-card>
          <v-card-title class="logout-header">
            {{ $t('token.logout_page_title') }}
          </v-card-title>
          <v-divider />
          <v-card-text>
            <p class="logout-text mb-0">
              {{ $t('token.logout_page_info') }}
            </p>
          </v-card-text>
          <v-card-actions class="mb-0 pb-3">
            <v-spacer />
            <v-btn
              color="sz_violet"
              flat
              class="mb-0 pb-0"
              @click="modalLogoutInfo = false"
            >
              {{ $t('token.modal_ok') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-content>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useStore } from '@/store';
import AppFooter from '@/components/widgets/AppFooter';
import validationRules from '@/constants/validationRules';

const props = defineProps({
    logoutReason: {
        type: Boolean,
        default: false
    }
});

const store = useStore();

const formData = ref({
    email: '',
    password: ''
});

const email = computed({
    get () {
        return formData.value.email;
    },
    set (nextEmailValue) {
        formData.value.email = nextEmailValue.trim();
        instance?.proxy?.$validator.validate('email', formData.value.email);
    }
});

const env = Object.freeze({
    forgotPassword: process.env.VUE_APP_FORGOT_PASSWORD_URL
});

const authError = ref('');
const hasError = ref(false);
const modalLogoutInfo = ref(false);

const instance = getCurrentInstance();

const isLoading = computed(() => store.getters.getIsLoading);

onMounted(() => {
    if (props.logoutReason) {
        modalLogoutInfo.value = true;
    }
});

async function validateAll () {
    return instance?.proxy?.$validator.validateAll();
}

const submit = async () => {
    store.commit('token/setForcedToLogout', false);

    const result = await validateAll();

    if (result) {
        authentication();
    }
};

function authentication () {
    authError.value = false;

    store
        .dispatch('login', formData.value)
        .then(() => {
            instance?.proxy?.$gtm.trackEvent({
                event: 'Login',
                category: 'login_button',
                action: 'click',
                label: 'User id',
                value: {
                    userId: JSON.parse(localStorage.getItem('user')).id
                }
            });
        })
        .catch((err) => {
            store.commit('setIsLoading', false);

            if (err?.response?.status === 400) {
                instance?.proxy?.errors.add({ field: 'password', msg: instance?.proxy?.$t('errors.password_or_mail') });
                instance?.proxy?.errors.add({ field: 'email', msg: instance?.proxy?.$t('errors.password_or_mail') });
                hasError.value = true;
            }
        });
}

function resetEmailValidation () {
    instance?.proxy?.errors.remove('email');
    hasError.value = false;
}

</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';
.list {
  li {
    float: left;
    padding: 5px 10px;
    list-style: none;
  }
  li:first-of-type {
    padding-left: 0;
  }
  li:last-of-type {
    padding-right: 0;
  }
}
.separator {
  border-right: 1px solid #999;
}
.right {
  float: right;
}
.logout-header {
  color: $sz_grafit;
  font-size: 16px;
}
.logout-text {
  color: $sz_dark_grey;
  font-size: 16px;
}
</style>
